import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Package, 
  Clock, 
  TrendingUp, 
  AlertTriangle,
  Camera as CameraIcon,
  MessageSquare,
  Image as ImageIcon,
  Activity,
  PackagePlus,
  RotateCcw,
  Trash2,
  Eye,
  Play,
  CheckSquare,
  Cloud
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import LogPickModal from "@/components/LogPickModal";
import LogReturnModal from "@/components/LogReturnModal";
import LogWasteModal from "@/components/LogWasteModal";
import PickLogModal from "@/components/PickLogModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PalletLog from "@/components/PalletLog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

const calculateActualUsage = (lots) => {
  if (!lots || lots.length === 0) return 0;
  
  const totalPicks = lots.reduce((sum, lot) => {
    const picks = lot.picks || [];
    return sum + picks.reduce((pickSum, pick) => pickSum + pick.picked_qty, 0);
  }, 0);
  
  const totalReturns = lots.reduce((sum, lot) => {
    const returns = lot.returns || [];
    return sum + returns.reduce((retSum, ret) => retSum + ret.returned_qty, 0);
  }, 0);
  
  return totalPicks - totalReturns;
};

const calculateTheoreticalUsage = (plannedQty, actualProduced, totalPlanned) => {
  if (!actualProduced || actualProduced === 0) return 0;
  return (plannedQty / totalPlanned) * actualProduced;
};

const calculateYield = (actual, theoretical) => {
  if (!actual || actual === 0) return 0;
  if (!theoretical || theoretical === 0) return 0;
  return ((theoretical / actual) * 100).toFixed(2);
};

const getYieldColor = (yieldPct) => {
  if (yieldPct === 0 || yieldPct === '0.00') return 'text-gray-400';
  if (yieldPct > 97.5) return 'text-green-600';
  if (yieldPct >= 90) return 'text-orange-500';
  return 'text-red-600';
};

const WorkOrderDetailPage = () => {
  const { wo_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPickModal, setShowPickModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showPickLogModal, setShowPickLogModal] = useState(false);
  const [pickLogComponent, setPickLogComponent] = useState(null);
  const [showQADialog, setShowQADialog] = useState(false);
  const [selectedQAInspection, setSelectedQAInspection] = useState(null);
  const [qaStatus, setQAStatus] = useState('');
  const [qaResultNotes, setQAResultNotes] = useState('');
  const [chartTimeRange, setChartTimeRange] = useState('last1hour'); // today, last4hours, last1hour
  
  const from = searchParams.get('from') || 'schedule';
  const backPath = from === 'dashboard' ? '/dashboard' : '/schedule';
  const backLabel = from === 'dashboard' ? 'Back to Production Dashboard' : 'Back to Schedule';

  // Generate dummy chart data for UPM based on time range
  const generateUPMData = () => {
    const data = [];
    const baseUPM = 75; // Base units per minute
    const targetUPM = 80;
    
    let dataPoints;
    if (chartTimeRange === 'today') {
      dataPoints = 480; // 8 hours (full production day)
    } else if (chartTimeRange === 'last4hours') {
      dataPoints = 240; // 4 hours
    } else {
      dataPoints = 60; // 1 hour
    }
    
    for (let i = 0; i < dataPoints; i++) {
      const variance = Math.sin(i / 10) * 8 + Math.random() * 5;
      const upm = baseUPM + variance;
      const minutesAgo = dataPoints - i;
      const timestamp = new Date(Date.now() - minutesAgo * 60000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      
      data.push({
        time: timestamp,
        upm: parseFloat(upm.toFixed(1)),
        target: targetUPM,
        variance: ((upm - targetUPM) / targetUPM * 100).toFixed(1)
      });
    }
    return data;
  };

  // Generate dummy chart data for Hourly Production based on time range
  const generateHourlyData = () => {
    const data = [];
    const targetPerHour = 120;
    
    // Fixed completed hours data (static values that don't change)
    const completedHours = {
      '06:00': 115,
      '07:00': 132,
      '08:00': 108,
      '09:00': 125,
      '10:00': 118,
      '11:00': 140,
      '12:00': 105,
      '13:00': 122
    };
    
    let hours;
    if (chartTimeRange === 'today') {
      hours = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00'];
    } else if (chartTimeRange === 'last4hours') {
      hours = ['06:00', '07:00', '08:00', '09:00'];
    } else {
      hours = ['08:00', '09:00'];
    }
    
    const currentHour = new Date().getHours();
    
    hours.forEach((hour, idx) => {
      const hourValue = parseInt(hour.split(':')[0]);
      const isCurrentHour = hourValue === currentHour;
      const isCompletedHour = hourValue < currentHour;
      
      // Use fixed values for completed hours, random for current hour
      let actual;
      if (isCompletedHour || completedHours[hour]) {
        actual = completedHours[hour] || 120;
      } else if (isCurrentHour) {
        // Current hour shows in-progress value (partial)
        actual = 50 + Math.floor(Math.random() * 40);
      } else {
        // Future hours show 0
        actual = 0;
      }
      
      data.push({
        hour,
        actual,
        target: targetPerHour,
        variance: ((actual - targetPerHour) / targetPerHour * 100).toFixed(1),
        opacity: isCurrentHour ? 0.6 : 1
      });
    });
    return data;
  };

  const upmData = generateUPMData();
  const hourlyData = generateHourlyData();

  useEffect(() => {
    const fetchWorkOrder = async () => {
      try {
        const response = await axios.get(`${API}/work-orders/${wo_id}`);
        setWorkOrder(response.data);
      } catch (error) {
        console.error("Error fetching work order:", error);
        toast.error("Failed to load work order details");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrder();
    
    // Set up polling for real-time updates
    const interval = setInterval(fetchWorkOrder, 5000);
    return () => clearInterval(interval);
  }, [wo_id]);

  const handleOpenPickModal = (component) => {
    setSelectedComponent(component);
    setShowPickModal(true);
  };

  const handleOpenReturnModal = (component) => {
    setSelectedComponent(component);
    setShowReturnModal(true);
  };

  const handleOpenWasteModal = (component) => {
    setSelectedComponent(component);
    setShowWasteModal(true);
  };

  const handleViewPickLog = (component) => {
    setPickLogComponent(component);
    setShowPickLogModal(true);
  };

  const handleOpenQADialog = (inspection) => {
    setSelectedQAInspection(inspection);
    setQAStatus(inspection.status);
    setQAResultNotes(inspection.result_notes || '');
    setShowQADialog(true);
  };

  const handleSubmitQA = async () => {
    if (!selectedQAInspection) return;
    
    try {
      await axios.post(`${API}/production/${wo_id}/log-qa-inspection`, null, {
        params: {
          inspection_id: selectedQAInspection.inspection_id,
          status: qaStatus,
          result_notes: qaResultNotes,
          inspected_by: 'qa_inspector'
        }
      });
      toast.success('QA inspection logged');
      setShowQADialog(false);
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to log QA inspection');
    }
  };

  const handleStageForProduction = async () => {
    try {
      await axios.post(`${API}/production/${wo_id}/stage-production`);
      toast.success('Work order staged for production');
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to stage work order');
    }
  };

  const handleStartProduction = async () => {
    try {
      await axios.post(`${API}/production/${wo_id}/start`);
      toast.success('Production started');
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to start production');
    }
  };

  const handleCompleteProduction = async () => {
    const confirmed = window.confirm(`Complete production for ${workOrder.wo_number}?`);
    if (confirmed) {
      try {
        await axios.post(`${API}/production/${wo_id}/complete-production`);
        toast.success('Production completed');
        // Refresh will happen via polling
      } catch (error) {
        toast.error('Failed to complete production');
      }
    }
  };

  const handleFinalize = async () => {
    const confirmed = window.confirm(
      `Mark ${workOrder.wo_number} as Finalized?\n\nThis will:\n1. Lock the work order\n2. Sync production data to NetSuite\n3. Create the Assembly Build transaction`
    );
    
    if (confirmed) {
      try {
        // Show loading toast
        const toastId = toast.loading("Finalizing and syncing to NetSuite...");

        // Call the new dedicated finalize endpoint
        const response = await axios.post(`${API}/production/${wo_id}/finalize`);
        
        // Update UI with the returned data
        setWorkOrder(response.data.work_order);
        
        toast.dismiss(toastId);
        toast.success('Work order finalized and synced successfully');
        
      } catch (error) {
        console.error("Error finalizing:", error);
        // Display the specific error message from the backend (e.g., if n8n fails)
        const errorMessage = error.response?.data?.detail || 'Failed to finalize work order';
        toast.error(`Error: ${errorMessage}`);
      }
    }
  };

  const handleLogPallet = async () => {
    // 1. Ask user for input
    const cases = window.prompt("Enter number of cases produced:", "50");
    
    // 2. If user clicked Cancel, stop
    if (!cases) return;

    try {
      // 3. Send the 'cases' in the body (this fixes the error)
      await axios.post(`${API}/production/${wo_id}/log-pallet`, { 
        cases: parseFloat(cases) 
      });
      
      toast.success(`Logged ${cases} cases`);
      // Refresh will happen via polling
    } catch (error) {
      console.error(error);
      toast.error('Failed to log pallet');
    }
  };

  const handleLogDowntime = async () => {
    const reason = window.prompt('Enter downtime reason:');
    if (!reason) return;
    
    const minutes = window.prompt('Enter duration in minutes:');
    if (!minutes) return;
    
    try {
      await axios.post(`${API}/production/${wo_id}/log-downtime`, {
        reason_code: 'manual',
        reason_description: reason,
        duration_minutes: parseFloat(minutes),
        logged_by: 'user'
      });
      toast.success('Downtime logged');
      // Refresh will happen via polling
    } catch (error) {
      toast.error('Failed to log downtime');
    }
  };


  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-gray-500">Loading work order details...</div>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Work Order Not Found</h2>
          <Button onClick={() => navigate(backPath)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {backLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="work-order-detail-page p-6 bg-gray-50 min-h-screen">
      {/* Header with Back Button */}
      <div className="mb-6 flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </Button>
      </div>
      
      {/* Work Order Header Card - Reorganized with Action Buttons */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Left: Work Order Info with Item Image (1 column) */}
            <div className="lg:col-span-1">
              <div className="flex items-start gap-3 mb-4">
                {/* Item Image - Smaller */}
                <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border-2 border-gray-200 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_mfg-workorder-app/artifacts/2rmfbvf8_MFGIcon.png"
                    alt={workOrder.item_name}
                    className="w-12 h-12 object-contain"
                    style={{ backgroundColor: 'transparent' }}
                  />
                </div>
                
                {/* WO Info */}
                <div className="flex-1">
                  <h1 className="text-xl font-bold text-gray-900 mb-1">{workOrder.wo_number}</h1>
                  <h2 className="text-sm text-gray-600 mb-1">{workOrder.item_name}</h2>
                  <p className="text-xs text-gray-500 mb-2">Line: {workOrder.production_line}</p>
                  <Badge 
                    className={
                      workOrder.status === 'finalized' ? 'bg-green-100 text-green-800' :
                      workOrder.status === 'production_complete' ? 'bg-blue-100 text-blue-800' :
                      workOrder.status === 'in_production' ? 'bg-purple-100 text-purple-800' :
                      workOrder.status === 'staged' ? 'bg-yellow-100 text-yellow-800' :
                      workOrder.status === 'scheduled' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }
                  >
                    {workOrder.status.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-y-1 text-xs border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="font-semibold">{workOrder.planned_date ? format(new Date(workOrder.planned_date), 'MMM dd') : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Time:</span>
                  <span className="font-semibold">{workOrder.planned_start_time}-{workOrder.planned_end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Planned:</span>
                  <span className="font-semibold">{workOrder.planned_quantity} cases</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Actual:</span>
                  <span className="font-semibold text-blue-600">{workOrder.actual_quantity} cases</span>
                </div>
                {workOrder.pallets_completed > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pallets:</span>
                    <span className="font-semibold text-green-600">{workOrder.pallets_completed}</span>
                  </div>
                )}
                {workOrder.downtime_minutes > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Downtime:</span>
                    <span className="font-semibold text-red-600">{workOrder.downtime_minutes} min</span>
                  </div>
                )}
              </div>
            </div>

            {/* Middle: Charts (2 columns) */}
            <div className="lg:col-span-2">
              {/* Check if order is active or completed/finalized */}
              {(workOrder.status === 'scheduled' || workOrder.status === 'staged' || workOrder.status === 'in_production' || workOrder.status === 'production_complete') ? (
                <>
                  {/* Time Range Controls - Only for active orders */}
                  <div className="flex items-center justify-end gap-2 mb-3">
                    <span className="text-xs text-gray-500 mr-2">Time Range:</span>
                    <Button
                      size="sm"
                      variant={chartTimeRange === 'today' ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      onClick={() => setChartTimeRange('today')}
                    >
                      Today
                    </Button>
                    <Button
                      size="sm"
                      variant={chartTimeRange === 'last4hours' ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      onClick={() => setChartTimeRange('last4hours')}
                    >
                      Last 4 Hours
                    </Button>
                    <Button
                      size="sm"
                      variant={chartTimeRange === 'last1hour' ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      onClick={() => setChartTimeRange('last1hour')}
                    >
                      Last Hour
                    </Button>
                  </div>
                </>
              ) : (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This is a historical work order ({workOrder.status.replace(/_/g, ' ').toUpperCase()}) - showing recorded data only
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* UPM Chart with Multi-Colored Line */}
                <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Units Per Minute (UPM)</h3>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={upmData}>
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 8 }}
                    interval={chartTimeRange === 'today' ? 59 : chartTimeRange === 'last4hours' ? 29 : 9}
                  />
                  <YAxis 
                    domain={[60, 95]}
                    tick={{ fontSize: 10 }}
                    width={30}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const variance = parseFloat(data.variance);
                        return (
                          <div className="bg-white p-2 border rounded shadow-sm text-xs">
                            <p className="font-semibold">{data.time}</p>
                            <p>UPM: <span className="font-bold">{data.upm}</span></p>
                            <p className={variance > 0 ? 'text-green-600' : 'text-red-600'}>
                              {variance > 0 ? '+' : ''}{variance}%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Target line */}
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="#94a3b8" 
                    strokeDasharray="3 3" 
                    dot={false} 
                    strokeWidth={1}
                  />
                  {/* Actual UPM line */}
                  <Line 
                    type="monotone" 
                    dataKey="upm" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Right: Hourly Production Chart */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Hourly Production</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={hourlyData}>
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 150]} tick={{ fontSize: 10 }} width={30} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const pct = (data.actual / data.target * 100).toFixed(0);
                        return (
                          <div className="bg-white p-2 border rounded shadow-sm text-xs">
                            <p className="font-semibold">{data.hour}</p>
                            <p>Actual: <span className="font-bold">{data.actual}</span> cases</p>
                            <p>Target: {data.target} cases</p>
                            <p className={
                              pct >= 110 ? 'text-green-600' : 
                              pct >= 90 ? 'text-blue-600' : 
                              'text-red-600'
                            }>
                              {pct}% of target
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Target line (dashed) */}
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    stroke="#94a3b8" 
                    strokeDasharray="5 5"
                    dot={false} 
                    strokeWidth={2}
                  />
                  {/* Single bar with dynamic color */}
                  {hourlyData.map((entry, index) => {
                    const pct = (entry.actual / entry.target) * 100;
                    let barColor = '#3b82f6'; // Blue (90-110%)
                    if (pct >= 110) {
                      barColor = '#22c55e'; // Green (>110%)
                    } else if (pct < 90) {
                      barColor = '#ef4444'; // Red (<90%)
                    }
                    return (
                      <Bar 
                        key={index}
                        dataKey="actual" 
                        data={[entry]}
                        fill={barColor}
                        radius={[4, 4, 0, 0]}
                        opacity={entry.opacity || 1}
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
              </div>
            </div>

            {/* Right: Action Buttons (1 column) */}
            <div className="lg:col-span-1 border-l pl-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
              <div className="flex flex-col gap-2">
                {/* Scheduled Status: Show Stage for Production button */}
                {workOrder.status === 'scheduled' && (
                  <Button 
                    size="sm"
                    onClick={handleStageForProduction}
                    className="bg-yellow-600 hover:bg-yellow-700 w-full justify-start"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Stage for Production
                  </Button>
                )}

                {/* Staged Status: Show Start Production button */}
                {workOrder.status === 'staged' && (
                  <Button 
                    size="sm"
                    onClick={handleStartProduction}
                    className="bg-green-600 hover:bg-green-700 w-full justify-start"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Production
                  </Button>
                )}

                {/* In Production Status: Show action buttons */}
                {workOrder.status === 'in_production' && (
                  <>
                    <Button 
                      size="sm"
                      onClick={handleLogPallet}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Log Pallet
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleLogDowntime}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Log Downtime
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleCompleteProduction}
                      className="bg-blue-600 hover:bg-blue-700 w-full justify-start"
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Complete Production
                    </Button>
                  </>
                )}

                {/* Production Complete Status: Show finalize option */}
                {workOrder.status === 'production_complete' && (
                  <Button 
                    size="sm"
                    onClick={handleFinalize} 
                    className="bg-green-600 hover:bg-green-700 w-full justify-start"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Finalize Work Order
                  </Button>
                )}

                {/* Finalized Status: Show sync button */}
                {workOrder.status === 'finalized'}

                {/* Finalized Status: Show message */}
                {workOrder.status === 'finalized' && (
                  <Badge className="bg-green-100 text-green-800 text-xs px-3 py-2 justify-start">
                    <CheckSquare className="w-3 h-3 mr-2" />
                    Finalized
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {/* Main Content - Takes 3 columns */}
        <div className="xl:col-span-3 space-y-6">

          {/* QA Inspections Section - Hidden for now but keeping code intact */}
          {false && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5" />
                  QA Inspections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Daily Inspections */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Daily Inspections</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {workOrder.qa_inspections && workOrder.qa_inspections.filter(i => i.inspection_type === 'daily').map((inspection, idx) => (
                        <div 
                          key={idx} 
                          className="border rounded p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleOpenQADialog(inspection)}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <h5 className="font-semibold text-sm">{inspection.inspection_name}</h5>
                            <Badge 
                              variant="outline"
                              className={
                                inspection.status === 'passed' ? 'bg-green-50 text-green-700 border-green-300' :
                                inspection.status === 'failed' ? 'bg-red-50 text-red-700 border-red-300' :
                                'bg-gray-50 text-gray-700 border-gray-300'
                              }
                            >
                              {inspection.status.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">{inspection.description}</p>
                          {inspection.result_notes && (
                            <p className="text-xs text-gray-700 italic">"{inspection.result_notes}"</p>
                          )}
                          {inspection.inspected_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              {format(new Date(inspection.inspected_at), 'MMM dd, HH:mm')} • {inspection.inspected_by}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Item-Specific Inspections */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Item-Specific Inspections</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {workOrder.qa_inspections && workOrder.qa_inspections.filter(i => i.inspection_type === 'item_specific').map((inspection, idx) => (
                        <div 
                          key={idx} 
                          className="border rounded p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleOpenQADialog(inspection)}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <h5 className="font-semibold text-sm">{inspection.inspection_name}</h5>
                              <p className="text-xs text-gray-500">{inspection.frequency}</p>
                            </div>
                            <Badge 
                              variant="outline"
                              className={
                                inspection.status === 'passed' ? 'bg-green-50 text-green-700 border-green-300' :
                                inspection.status === 'failed' ? 'bg-red-50 text-red-700 border-red-300' :
                                'bg-gray-50 text-gray-700 border-gray-300'
                              }
                            >
                              {inspection.status.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">{inspection.description}</p>
                          {inspection.result_notes && (
                            <p className="text-xs text-gray-700 italic">"{inspection.result_notes}"</p>
                          )}
                          {inspection.inspected_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              {format(new Date(inspection.inspected_at), 'MMM dd, HH:mm')} • {inspection.inspected_by}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Charts Section - Keep as is but moved after QA */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Charts removed from here as they're now in header */}
          </div>

          {/* Main Content Split: BOM (Left) and Pallet Log (Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column: BOM (Takes up 2/3 space) */}
            <div className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Package className="w-5 h-5" />
                      Bill of Materials
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {workOrder.bom_usage && workOrder.bom_usage.length > 0 ? (
                      workOrder.bom_usage.map((component, idx) => {
                        // Handle BOM Reference entries differently
                        if (component.is_bom_reference) {
                          return (
                            <div key={idx} className="border-l-4 border-amber-500 pl-3 py-2 bg-amber-50 rounded-r">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-sm text-gray-900">📋 {component.component_name}</h4>
                                <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800">BOM Reference</Badge>
                              </div>
                              <div className="text-xs text-gray-600 space-y-1">
                                <p><span className="font-semibold">BOM ID:</span> {component.bom_id || 'N/A'}</p>
                                {component.bom_external_id && (
                                  <p><span className="font-semibold">External ID:</span> {component.bom_external_id}</p>
                                )}
                              </div>
                            </div>
                          );
                        }

                        // Regular component entries with Yield Calculation & Color Logic
                        const actualUsage = calculateActualUsage(component.lots);
                        const theoretical = calculateTheoreticalUsage(
                          component.planned_qty,
                          workOrder.actual_quantity,
                          workOrder.planned_quantity
                        );
                        const yieldPct = calculateYield(actualUsage, theoretical);
                        const yieldColorText = getYieldColor(yieldPct); // Gets text color class (e.g., text-red-600)

                        // Determine BORDER color based on yield text color
                        let borderColorClass = 'border-blue-500'; // Default Neutral/Not Started
                        if (actualUsage > 0) { 
                           if (yieldColorText.includes('green')) borderColorClass = 'border-green-500';
                           else if (yieldColorText.includes('orange')) borderColorClass = 'border-amber-500';
                           else if (yieldColorText.includes('red')) borderColorClass = 'border-red-500';
                        }

                        return (
                          <div key={idx} className={`border-l-4 ${borderColorClass} pl-3 py-2 bg-gray-50 rounded-r`}>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-sm text-gray-900">{component.component_name}</h4>
                              <Badge variant="outline" className="text-xs">{component.item_type}</Badge>
                            </div>

                            <div className="grid grid-cols-4 gap-2 mb-2 text-xs">
                              <div>
                                <p className="text-gray-500">Planned</p>
                                <p className="font-semibold">{component.planned_qty.toFixed(0)} {component.unit}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Actual</p>
                                <p className="font-semibold text-blue-600">{actualUsage.toFixed(1)} {component.unit}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Theoretical</p>
                                <p className="font-semibold">{theoretical.toFixed(1)} {component.unit}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Yield</p>
                                <p className={`font-semibold ${yieldColorText}`}>{yieldPct}%</p>
                              </div>
                            </div>

                            {/* Action Buttons - Preserved exactly as before */}
                            {(workOrder.status === 'staged' || workOrder.status === 'in_production' || workOrder.status === 'production_complete') && (
                              <div className="flex gap-1 flex-wrap">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-xs h-7"
                                  onClick={() => handleOpenPickModal(component)}
                                >
                                  <PackagePlus className="w-3 h-3 mr-1" />
                                  Pick
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-xs h-7"
                                  onClick={() => handleOpenReturnModal(component)}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Return
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-xs h-7"
                                  onClick={() => handleOpenWasteModal(component)}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Waste
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-xs h-7"
                                  onClick={() => handleViewPickLog(component)}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View Log
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        {workOrder.status === 'completed' || workOrder.status === 'finalized' ? (
                          <>
                            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                            <p className="text-sm">No BOM data available for this historical work order</p>
                            {workOrder.netsuite_data && (
                              <p className="text-xs text-gray-400 mt-2">
                                BOM: {workOrder.netsuite_data.billOfMaterials?.refName || 'N/A'}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm">No components assigned to this work order yet</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Pallet Log (Takes up 1/3 space) */}
            <div className="lg:col-span-1">
               <PalletLog 
                 workOrder={workOrder} 
                 onPalletAdded={() => {
                   // Optional callback
                 }} 
               />
            </div>
          </div>
        </div>

        {/* Right Sidebar - Activity Log */}
        <div className="xl:col-span-1">
          <Card className="sticky top-6 max-h-[calc(100vh-100px)] overflow-hidden flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5" />
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              <div className="space-y-3">
                {workOrder.activity_log && workOrder.activity_log.length > 0 ? (
                  [...workOrder.activity_log].reverse().map((activity, idx) => (
                    <div key={idx} className="border-l-2 border-blue-500 pl-2 py-1.5 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-2">
                        {activity.activity_type === 'pick' && <PackagePlus className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'return' && <RotateCcw className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'waste' && <Trash2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'comment' && <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'photo' && <ImageIcon className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'pallet_complete' && <Package className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'downtime' && <Clock className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                        {activity.activity_type === 'status_change' && <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />}
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 leading-tight">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {activity.timestamp ? format(new Date(activity.timestamp), 'HH:mm') : 'N/A'} • {activity.user}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-8">No activity logged yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          {workOrder.comments && workOrder.comments.length > 0 && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="w-4 h-4" />
                  Comments ({workOrder.comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {workOrder.comments.map((comment, idx) => (
                    <div key={idx} className="bg-gray-50 rounded p-2 text-xs">
                      <p className="text-gray-900">{comment.comment_text}</p>
                      <p className="text-gray-500 mt-1">
                        {comment.created_at ? format(new Date(comment.created_at), 'MMM dd, HH:mm') : 'N/A'} • {comment.created_by}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Photos Section */}
          {workOrder.photos && workOrder.photos.length > 0 && (
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ImageIcon className="w-4 h-4" />
                  Photos ({workOrder.photos.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {workOrder.photos.map((photo, idx) => (
                    <div key={idx} className="border rounded overflow-hidden relative group">
                      {/* Action Buttons Overlay */}
                      <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 p-1 rounded">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(photo.photo_url, '_blank');
                          }}
                          className="text-white hover:text-blue-200 p-1"
                          title="View Full Size"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm("Delete this photo?")) return;
                            try {
                              await axios.delete(`${API}/production/${workOrder.wo_id}/photos/${photo.photo_id}`);
                              toast.success("Photo deleted");
                              // Refresh logic is handled by socket/polling usually, but force refresh if needed:
                              // const res = await axios.get(`${API}/work-orders/${wo_id}`);
                              // setWorkOrder(res.data);
                            } catch (err) {
                              toast.error("Failed to delete photo");
                            }
                          }}
                          className="text-white hover:text-red-200 p-1"
                          title="Delete Photo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Image */}
                      <img 
                        src={photo.photo_url} 
                        alt={photo.caption || 'Work order photo'} 
                        className="w-full h-auto cursor-pointer"
                        onClick={() => window.open(photo.photo_url, '_blank')}
                      />
                      
                      {/* Caption & Metadata */}
                      {photo.caption && <p className="text-xs text-gray-700 p-2">{photo.caption}</p>}
                      <p className="text-xs text-gray-500 px-2 pb-2">
                        {photo.uploaded_at ? format(new Date(photo.uploaded_at), 'MMM dd, HH:mm') : 'N/A'} • {photo.uploaded_by || 'User'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPickModal && selectedComponent && (
        <LogPickModal
          isOpen={showPickModal}
          onClose={() => setShowPickModal(false)}
          workOrder={workOrder}
          component={selectedComponent}
          onSuccess={() => {
            setShowPickModal(false);
            // Refresh will happen via polling
          }}
        />
      )}

      {showReturnModal && selectedComponent && (
        <LogReturnModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          workOrder={workOrder}
          component={selectedComponent}
          onSuccess={() => {
            setShowReturnModal(false);
          }}
        />
      )}

      {showWasteModal && selectedComponent && (
        <LogWasteModal
          isOpen={showWasteModal}
          onClose={() => setShowWasteModal(false)}
          workOrder={workOrder}
          component={selectedComponent}
          onSuccess={() => {
            setShowWasteModal(false);
          }}
        />
      )}

      {/* Pick Log Modal */}
      {showPickLogModal && pickLogComponent && (
        <PickLogModal
          isOpen={showPickLogModal}
          onClose={() => setShowPickLogModal(false)}
          component={pickLogComponent}
          workOrder={workOrder}
        />
      )}

      {/* QA Inspection Dialog */}
      {showQADialog && selectedQAInspection && (
        <Dialog open={showQADialog} onOpenChange={setShowQADialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedQAInspection.inspection_name}</DialogTitle>
              <p className="text-sm text-gray-500">{selectedQAInspection.description}</p>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="qaStatus">Status</Label>
                <select
                  id="qaStatus"
                  value={qaStatus}
                  onChange={(e) => setQAStatus(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="pending">Pending</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="qaResultNotes">Result Notes</Label>
                <textarea
                  id="qaResultNotes"
                  value={qaResultNotes}
                  onChange={(e) => setQAResultNotes(e.target.value)}
                  placeholder="Enter inspection results..."
                  rows={4}
                  className="w-full p-2 border rounded-md"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmitQA}>
                Log Inspection
              </Button>
              <Button variant="outline" onClick={() => setShowQADialog(false)}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* NetSuite Data Modal */}

    </div>
  );
};

export default WorkOrderDetailPage;
